import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import StatusChip from "../components/StatusChip";
import api, { formatPrice, formatTime, tzDateKey, tzMidnight } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";

function KpiCard({ label, value, sub, testid }) {
  return (
    <div className="bg-white border border-hairline rounded-lg shadow-warm p-6" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">{label}</p>
      <p className="font-ui text-3xl font-semibold text-charcoal mt-2">{value}</p>
      {sub && <p className="text-xs font-ui text-slate mt-1.5">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [kpi, setKpi] = useState(null);
  const [appts, setAppts] = useState([]);
  const [lowBalance, setLowBalance] = useState([]);
  const [refills, setRefills] = useState([]);

  useEffect(() => {
    // Use practice-TZ day boundaries so the schedule list matches the KPI endpoint,
    // which evaluates "today" in the practice timezone (Chicago Central).
    const todayKey = tzDateKey(new Date());
    const start = tzMidnight(todayKey).toISOString();
    const [y, m, d] = todayKey.split("-").map(Number);
    const tomorrowKey = tzDateKey(new Date(Date.UTC(y, m - 1, d + 1, 12)));
    const end = tzMidnight(tomorrowKey).toISOString();

    api.get("/reports/kpi").then(({ data }) => setKpi(data)).catch(() => {});
    api.get("/appointments", { params: { date_from: start, date_to: end } })
      .then(({ data }) => setAppts(data)).catch(() => {});
    api.get("/packages/low-balance").then(({ data }) => setLowBalance(data)).catch(() => {});
    api.get("/refills/upcoming").then(({ data }) => setRefills(data)).catch(() => {});
  }, []);

  return (
    <AdminLayout>
      {/* Greeting */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-sage mb-2" data-testid="dashboard-eyebrow">— Overview</p>
          <h1 className="font-display text-4xl text-charcoal">Welcome back, {user?.name?.split(" ")[0] || "friend"}.</h1>
          <p className="text-sm font-ui text-slate mt-1">Your day at The Root Forum.</p>
        </div>
        <Link to="/admin/schedule" className="px-5 py-2.5 bg-forest hover:bg-charcoal text-white rounded-md text-sm font-medium font-ui transition-colors" data-testid="quick-action-schedule">
          Open schedule
        </Link>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard label="Appointments Today" value={kpi?.appointments_today ?? "—"} sub={`Completed ${kpi?.completed ?? 0} · Waiting ${kpi?.waiting ?? 0}`} testid="kpi-appointments" />
        <KpiCard label="Today's Revenue" value={kpi ? formatPrice(kpi.todays_revenue) : "—"} sub="Visits + packages" testid="kpi-revenue" />
        <KpiCard label="Outstanding" value={kpi?.outstanding_count ?? "—"} sub="Confirmed, unpaid" testid="kpi-outstanding" />
        <KpiCard label="Active Packages" value={kpi?.active_packages ?? "—"} sub="With sessions remaining" testid="kpi-packages" />
      </div>

      {/* Schedule + alerts */}
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 bg-white border border-hairline rounded-lg shadow-warm">
          <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
            <h2 className="font-ui font-semibold text-charcoal">Today's schedule</h2>
            <Link to="/admin/schedule" className="text-xs font-ui text-sage hover:text-forest">See all →</Link>
          </div>
          <div className="divide-y divide-hairline" data-testid="todays-schedule-list">
            {appts.length === 0 && <div className="px-6 py-12 text-center text-sm font-ui text-slate">No appointments yet today — let's schedule one.</div>}
            {appts.map((a) => (
              <div key={a.id} className="px-6 py-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="font-mono text-sm text-charcoal w-14">{formatTime(a.scheduled_start)}</div>
                  <div className={`w-1 self-stretch rounded-pill ${statusToColor(a.status)}`} />
                  <div className="min-w-0">
                    <p className="font-ui text-sm text-charcoal truncate">
                      {a.client ? `${a.client.first_name} ${a.client.last_initial}.` : "—"}
                      <span className="text-slate"> · </span>
                      {a.service?.name}
                    </p>
                    <p className="text-xs font-ui text-slate truncate">{a.practitioner?.display_name}</p>
                  </div>
                </div>
                <StatusChip status={a.status} />
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white border border-hairline rounded-lg shadow-warm">
            <div className="px-6 py-4 border-b border-hairline">
              <h2 className="font-ui font-semibold text-charcoal">Low-balance clients</h2>
            </div>
            <div className="px-6 py-4">
              {lowBalance.length === 0 && <p className="text-sm font-ui text-slate">All clients have packages with healthy balances.</p>}
              <ul className="space-y-3" data-testid="low-balance-list">
                {lowBalance.map((p) => (
                  <li key={p.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-ui text-charcoal">{p.client?.first_name} {p.client?.last_initial}.</p>
                      <p className="text-xs font-ui text-slate">{p.package_type?.name}</p>
                    </div>
                    <span className="font-mono text-xs text-status-warning bg-status-warning-bg px-2 py-1 rounded-pill">{p.remaining} left</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-white border border-hairline rounded-lg shadow-warm">
            <div className="px-6 py-4 border-b border-hairline">
              <h2 className="font-ui font-semibold text-charcoal">Refill follow-up</h2>
            </div>
            <div className="px-6 py-4">
              {refills.length === 0 && <p className="text-sm font-ui text-slate">No tinctures due for refill in the next 2 days.</p>}
              <ul className="space-y-3" data-testid="refills-list">
                {refills.slice(0, 4).map((r) => (
                  <li key={r.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-ui text-charcoal">{r.client?.first_name} {r.client?.last_initial}.</p>
                      <p className="text-xs font-ui text-slate">{r.product_name} · refill {new Date(r.estimated_refill_at).toLocaleDateString()}</p>
                    </div>
                    <Link to="/admin/refills" className="text-xs font-ui text-sage hover:text-forest">Open →</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function statusToColor(status) {
  switch (status) {
    case "completed": return "bg-status-success";
    case "checked_in":
    case "in_progress": return "bg-status-info";
    case "held":
    case "confirmed": return "bg-status-warning";
    case "canceled":
    case "no_show": return "bg-status-danger";
    default: return "bg-hairline";
  }
}
